import { Button, Dialog } from '@web_opxp/components';
import { FC, PropsWithChildren, ReactNode, createContext, useContext, useMemo, useState } from 'react';
import QuestionIcon from '../QuestionIcon/QuestionIcon';
import './HintPopup.scss';

export interface IHintPopupProps {
  className?: string;
  title?: ReactNode;
  iconSize?: number;
  okButtonLabel?: string;
}

interface IHintPopupContext {
  visible: boolean;
  openDialog: () => void;
  closeDialog: () => void;
}

// DevNote: please note that this is just an experiment and proof of concept
// its not necessary to use context for such simple things
const HintPopupContext = createContext<IHintPopupContext | undefined>(undefined);

export const HintPopup: FC<PropsWithChildren<IHintPopupProps>> = ({
  children,
  className,
  title,
  iconSize = 16,
  okButtonLabel,
}) => {
  const [show, setShow] = useState(false);
  const customClass = className ? ` ${className}` : '';
  const contextValue = useMemo(() => {
    const closeDialog = () => setShow(false);
    const openDialog = () => setShow(true);
    const value: IHintPopupContext = {
      visible: show,
      openDialog,
      closeDialog,
    };
    return value;
  }, [show, setShow]);
  return (
    <HintPopupContext.Provider value={contextValue}>
      <span
        className={`hint-popup${customClass}`}
        style={{ lineHeight: `${iconSize}px` }}
        onClick={contextValue.openDialog}>
        <QuestionIcon height={iconSize} width={iconSize} />
      </span>
      <Dialog
        title={title}
        autoFocus={true}
        usePortal={true}
        isOpen={show}
        onClose={contextValue.closeDialog}
        className="hint-popup-dialog"
        footerContent={okButtonLabel && <Button label={okButtonLabel} onClick={contextValue.closeDialog} />}>
        {children}
      </Dialog>
    </HintPopupContext.Provider>
  );
};

export function useHintPopup() {
  const value = useContext(HintPopupContext);
  if (!value) {
    throw new Error('This hook must be used only by child components of the HintPopup!');
  }
  return value;
}

export default HintPopup;
